import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { AuthButton } from "@/components/auth-button";
import { VowQuizLogo } from "@/components/icons/VowQuizLogo";
import { useState, useEffect } from "react";
import { getUserQuizzes } from "@/lib/quiz-api";
import { useToast } from "@/hooks/use-toast";

export function NavBar() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [userQuiz, setUserQuiz] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Fetch the user's quiz when the component mounts or user changes
  useEffect(() => {
    async function fetchUserQuiz() {
      if (!user) return;
      
      try {
        setLoading(true);
        const quizzes = await getUserQuizzes();
        if (quizzes && quizzes.length > 0) {
          // Store the internal ID of the first quiz
          setUserQuiz(quizzes[0].internalId);
        }
      } catch (error) {
        console.error("Error fetching user quiz:", error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchUserQuiz();
  }, [user]);
  
  const handleMyQuizClick = () => {
    if (userQuiz) {
      // If user has a quiz, go directly to it
      setLocation(`/quiz/${userQuiz}`);
    } else {
      // Otherwise go to quizzes page (or create a new quiz)
      setLocation("/quizzes");
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 shadow-sm">
      <div className="container mx-auto h-14 flex items-center justify-between max-w-screen-2xl px-6">
        {/* Logo */}
        <Button
          variant="ghost"
          className="flex items-center space-x-2 hover:bg-pink-50 transition-colors"
          onClick={() => setLocation("/")}
        >
          <VowQuizLogo width={50} height={50} />
          <span className="font-serif text-lg text-gray-800 hidden md:inline">
            Vow Quiz
          </span>
        </Button>

        {/* Navigation Links */}
        <nav className="flex items-center space-x-4 lg:space-x-6 mx-6">
          <Button
            variant="ghost"
            className="text-base font-medium text-gray-700 hover:text-pink-600 transition-colors"
            onClick={() => setLocation("/pricing")}
          >
            Pricing
          </Button>
          {user && (
            <Button
              variant="ghost"
              className="text-base font-medium text-gray-700 hover:text-pink-600 transition-colors"
              onClick={handleMyQuizClick}
              disabled={loading}
            >
              My Quiz
            </Button>
          )}
        </nav>

        {/* Auth Button */}
        <div className="ml-auto flex items-center space-x-4">
          <AuthButton className="bg-white text-gray-800 border-gray-300 hover:bg-gray-100 hover:border-gray-400 gap-2 transition-colors" />
        </div>
      </div>
    </div>
  );
}
